import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VehData, WebOp, WebOpChecked, WebOpData } from 'src/models';
import { TestWebOp } from 'src/models/WebOp/TestWebOp';
import { AxiosError } from 'axios';
import * as acBase from 'src/store/states/GenericActionCreator';
import { IStoreState } from 'src/store';
import { ViewMode } from 'src/enums';

/** Interfaces */
export interface IWebOpsState {
    WebOps: WebOp[];
    checkedWebOps: WebOpChecked[];
    built: WebOpData;
    returnCode?: string;
}

export const initialWebopData: WebOpData = {
    built: false,
};

/** Initial State */
export const initialState: IWebOpsState = {
    WebOps: [],
    checkedWebOps: [],
    built: initialWebopData,
};

export const WebOpsSlice = createSlice({
    name: 'WebOps',
    initialState: initialState as IWebOpsState,
    reducers: {
        getWebOps: (state, action: PayloadAction<WebOp[]>) => {
            const apptOps = action.payload?.map(
                (operation) => {
                    let itemCostHigh = '';
                    let itemCostLow = '';
                    if (!operation.itemCostHigh && !operation.itemCostLow) {
                        // If neither price limit exists we send the **** which causes the
                        // Call Dealer for Pricing literal to display.
                        itemCostHigh = '****';
                        itemCostLow = '****';
                    } else {
                        // Checks if the price limit was returned as undefined or contains letters
                        // If both are returned this way, replace with same literal as above.
                        itemCostHigh = (operation.itemCostHigh !== undefined && !/[a-zA-Z]/.test(operation.itemCostHigh)) ?
                            operation.itemCostHigh : '';
                        itemCostLow = (operation.itemCostLow !== undefined && !/[a-zA-Z]/.test(operation.itemCostLow)) ?
                            operation.itemCostLow : '';
                        if (itemCostHigh === '' && itemCostHigh === itemCostLow) {
                            itemCostHigh = '****';
                            itemCostLow = '****';
                        }
                    }


                    return ({
                        ...operation,
                        itemCostHigh: itemCostHigh,
                        itemCostLow: itemCostLow,
                    });
                },
            );
            state.built.built = true;

            state.WebOps = apptOps;
        },
        toggleCheck: (state, action: PayloadAction<WebOpChecked[]>) => {
            state.checkedWebOps = action.payload;
        },
        clearChecks: (state) => {
            state.checkedWebOps = [];
        },
        setError: (state, action: PayloadAction<string>) => {
            state.returnCode = action.payload;
        },
        toggleLangLits: (state, action: PayloadAction<WebOp[]>) => {
            state.WebOps = action.payload;
        },

    },
});

export const {
    getWebOps,
    toggleCheck,
    clearChecks,
    setError,
    toggleLangLits,
} = WebOpsSlice.actions;

export default WebOpsSlice.reducer;

export function GetApptOps(profileNum: string, store: string, branch: string, vehData?: VehData) {
    return async (dispatch, getState: () => IStoreState) => {
        let vin: string | undefined = '';
        let make: string | undefined = '';
        let model: string | undefined = '';
        let year: string | undefined = '';
        const selectedVehicle = getState().Vehicles?.selectedVehicle!;
        if (vehData) {
            vin = vehData.vin;
            make = vehData.make;
            model = vehData.model;
            year = vehData.year;
        } else {
            vin = getState().Appointment.ApptVeh.vin;
            make = getState().Appointment.ApptVeh.make;
            model = getState().Appointment.ApptVeh.model;
            year = getState().Appointment.ApptVeh.vehYr;
        }
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            functionFlag: 'O',
            profileNum: profileNum,
            vin: vin,
            make: make,
            model: model,
            year: year,
            callingProgram: callingProgram,
            logId: logId,
            keyCustNum: selectedVehicle,
        };

        let response = await acBase.eraWebServiceCall<WebOp[]>('SVPController', 'GetSpecs', reqData, store, branch);
        if (response.returnCode !== '8000') {
            // Temp code to change response to default data if the PICK call failed.
            if (response && (response as AxiosError).isAxiosError) {
                // replace with BlankWebOp after testing is done
                // response = [{...BlankWebOp}] as WebOp[];
                response = TestWebOp;
            }
            dispatch(getWebOps(response.webOps));
        } else {
            dispatch(setError(response.returnCode));
        }
    };
}

export function ToggleItems(id) {
    return (dispatch, getState: () => IStoreState) => {
        let checkedItem = [...getState().WebOps.checkedWebOps];
        const itemIndex = checkedItem.findIndex(result => result.id === id);
        if (itemIndex === -1) {
            checkedItem.push({ id: id });
        } else {
            checkedItem = checkedItem.filter(result => result.id !== id);
        }
        dispatch(toggleCheck(checkedItem));
    };
}

export function ClearCheckedWebOps() {
    return (dispatch) => {
        dispatch(clearChecks());
    };
}

export function ToggleOpsLangLits(language: string) {
    return (dispatch, getState: () => IStoreState) => {
        const toggleOpt = getState().Specs.specs?.langOption;
        const WebOps = getState().WebOps;
        let responseWebOps = WebOps;
        if (language === 'fr-CA') {
            const WebOpsCopy: WebOp[] = [];
            const WebOpsA = responseWebOps.WebOps;
            WebOpsA?.forEach((WebOp) => {
                WebOpsCopy.push({
                    id: WebOp.id,
                    webTitle: WebOp.webTitleFR,
                    webTitleEN: WebOp.webTitleEN,
                    webTitleFR: WebOp.webTitleFR,
                    estTime: WebOp.estTime,
                    spgOp: WebOp.spgOp,
                    itemCostLow: WebOp.itemCostLow,
                    itemCostHigh: WebOp.itemCostHigh,
                    opPriority: WebOp.opPriority,
                    cause: WebOp.cause,
                    urls: WebOp.urls,
                    why: WebOp.why,
                    what: WebOp.what,
                    reason: WebOp.reason,
                    department: WebOp.department,
                    opWaiter: WebOp.opWaiter,
                    opLoaner: WebOp.opLoaner,
                    opRental: WebOp.opRental,
                    opConcierge: WebOp.opConcierge,
                    opCadillac: WebOp.opCadillac,
                    opPPUDWL: WebOp.opPPUDWL,
                    complaintDef: WebOp.complaintDef,
                });
            });
            responseWebOps = { ...responseWebOps, WebOps: WebOpsCopy };
        } else {
            const WebOpsCopy: WebOp[] = [];
            const WebOpsA = responseWebOps.WebOps;
            WebOpsA?.forEach((WebOp) => {
                WebOpsCopy.push({
                    id: WebOp.id,
                    webTitle: WebOp.webTitleEN,
                    webTitleEN: WebOp.webTitleEN,
                    webTitleFR: WebOp.webTitleFR,
                    estTime: WebOp.estTime,
                    spgOp: WebOp.spgOp,
                    itemCostLow: WebOp.itemCostLow,
                    itemCostHigh: WebOp.itemCostHigh,
                    opPriority: WebOp.opPriority,
                    cause: WebOp.cause,
                    urls: WebOp.urls,
                    why: WebOp.why,
                    what: WebOp.what,
                    reason: WebOp.reason,
                    department: WebOp.department,
                    opWaiter: WebOp.opWaiter,
                    opLoaner: WebOp.opLoaner,
                    opRental: WebOp.opRental,
                    opConcierge: WebOp.opConcierge,
                    opCadillac: WebOp.opCadillac,
                    opPPUDWL: WebOp.opPPUDWL,
                    complaintDef: WebOp.complaintDef,
                });
            });
            responseWebOps = { ...responseWebOps, WebOps: WebOpsCopy };
        }
        if (toggleOpt === 'Y') {
            dispatch(toggleLangLits(responseWebOps.WebOps));
        }
    };
}